import { DeviceType } from '@/types/api';

// Per Dave (Norgas hardware), 2026-05-04 email:
//   - DevEUI bit 26 (counting from zero) is `0` for water meters and `1` for
//     SALT transmitters. Mask `0x0000000004000000`.
//   - JoinEUI distinguishes the two as a secondary signal:
//       Meter:  0x8c1f644d005ed2ac
//       SALT :  0x8c1f644d0427c3de
//
// Some pre-2026-05 SALT boards were flashed with the meter's JoinEUI AND
// without bit 26 set — those will read as `WaterMeter` until they're
// re-flashed (and corrected automatically once the device transmits, via
// the FPort-based inference in `DeviceService`/decoder Lambda). The check
// below is best-effort at setup time; the backend remains the source of
// truth on first uplink.

const SALT_DEVEUI_BIT_MASK = 0x0000000004000000n;
const SALT_JOIN_EUI = '8c1f644d0427c3de';

function normalizeHex(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(/[^0-9a-f]/gi, '').toLowerCase();
}

/**
 * Best-effort classification of a meter's device type from its EUIs at setup
 * time, before any uplink has been received.
 *
 *   - Primary: DevEUI bit 26 (most reliable signal post-re-flash).
 *   - Secondary: JoinEUI === SALT JoinEUI (catches mid-transition boards).
 *
 * Returns `WaterMeter` when both signals are absent or the input is malformed
 * — that's the safer default since the FPort-based inference on first
 * transmission will correct any mis-classification.
 */
export function detectDeviceType(devEui: string, joinEui?: string | null): DeviceType {
  const dev = normalizeHex(devEui);
  if (dev.length === 16) {
    try {
      const devBig = BigInt('0x' + dev);
      if ((devBig & SALT_DEVEUI_BIT_MASK) !== 0n) {
        return DeviceType.SALTTransmitter;
      }
    } catch {
      // BigInt parse fail — fall through to JoinEUI check below.
    }
  }

  const join = normalizeHex(joinEui);
  if (join === SALT_JOIN_EUI) {
    return DeviceType.SALTTransmitter;
  }

  return DeviceType.WaterMeter;
}
